import type { quantityDropdownValue, valueType } from '/business/shared/components/pages/question/types';
import type { QuestionConfigQuestionType } from '/index';
import type { emissionDropdown } from '/index';

import { capitalGoodsSpendCategories } from '/business/shared/components/questions/scope3/goods/capitalGoods/helpers/helpers';
import { purchasedGoodsAndServicesSpendCategories } from '/business/shared/components/questions/scope3/goods/purchasedGoods/helpers/helpers';
import { useGetEmissionsFromQuestion } from '/business/shared/hooks/useGetEmissionsFromQuestion';
import { useSelector } from 'react-redux';
import getRefrigerantsSelector from '/business/shared/state/selectors/form/getRefrigerantsSelector';
import getDeicingChemicalTypesSelector from '/business/shared/state/selectors/form/getDeicingChemicalTypesSelector';
import type { EmissionTypeOption } from '/business/shared/state/reducers/emissionTypes';

export const useGetDistanceDropdownValues = (): quantityDropdownValue[] => {
  return [
    { name: 'km', displayName: 'km' },
    { name: 'miles', displayName: 'miles' },
  ];
};

export const useGetUnitDropdownValues = (isAbsoluteUnits: boolean): quantityDropdownValue[] => {
  if (isAbsoluteUnits) {
    return [
      { name: 'kg', displayName: 'kgCO₂e' },
      { name: 'tonne', displayName: 'tCO₂e' },
    ];
  } else {
    return [
      { name: 'kg', displayName: 'kgCO₂e/Unit' },
      { name: 'tonne', displayName: 'tCO₂e/Unit' },
    ];
  }
};

export const useGetQuantityDropdownValues = ({
  questionFormatName,
  emissionSource,
  emissionSourcesArray,
  allRelevantUnits = false,
}: {
  questionFormatName: string;
  emissionSource?: string | null;
  emissionSourcesArray?: emissionDropdown[];
  allRelevantUnits?: boolean;
}): quantityDropdownValue[] => {
  if (questionFormatName === 'purchased-goods-and-services' || questionFormatName === 'capital-goods') {
    return [{ name: 'unit', displayName: 'Unit(s)' }];
  } else if (
    questionFormatName === 'other' ||
    questionFormatName === 'on-site-power-generation' ||
    questionFormatName === 'boilers-and-furnaces' ||
    questionFormatName === 'on-site-water-treatment' ||
    questionFormatName === 'upstream-leased-buildings' ||
    questionFormatName === 'downstream-leased-buildings' ||
    questionFormatName === 'sold-products-fuels' ||
    questionFormatName === 'vehicles' ||
    (questionFormatName === 'waste-generated' && allRelevantUnits)
  ) {
    if (allRelevantUnits) {
      return (emissionSourcesArray || []).reduce((acc: quantityDropdownValue[], current) => {
        const units = current.units || [];
        const filtered = units.filter((unit) => {
          const unitName = typeof unit === 'string' ? unit : unit?.name;
          if (unitName) {
            const located = acc.find((needle) => {
              return needle.name === unitName;
            });
            return !located;
          } else if (unit) {
            return !acc.includes(unit as quantityDropdownValue);
          }
        });
        return acc.concat(
          filtered.map((unit) => {
            if (typeof unit === 'string') {
              return { name: unit, displayName: unit };
            }
            return unit;
          }),
        );
      }, []);
    }

    if (!emissionSource || !emissionSourcesArray) {
      return [];
    }
    const rawUnits = emissionSourcesArray?.find(({ name }) => name === emissionSource)?.units || [];
    return rawUnits.map((unit): quantityDropdownValue => {
      if (typeof unit === 'string') {
        return { name: unit, displayName: unit };
      }
      return unit;
    });
  } else if (
    questionFormatName === 'refrigerants' ||
    questionFormatName === 'waste-generated' ||
    questionFormatName === 'deicing'
  ) {
    const units = emissionSourcesArray?.find(({ name }) => name === emissionSource)?.units;
    return units
      ? units.map((unit) => {
          const unitName = typeof unit === 'string' ? unit : unit.name;
          const unitDisplayName = typeof unit === 'string' ? unit : unit.displayName;
          return { name: unitName, displayName: unitDisplayName };
        })
      : [];
  }
  return [];
};

// TODO: [WR] refactor after adding config for supplier and custom questions
export const useGetEmissionSourceFields = ({
  questionFormatName,
  wasteTypes,
}: {
  questionFormatName: string;
  question?: QuestionConfigQuestionType;
  wasteTypes?: EmissionTypeOption[];
}): valueType[] => {
  const { mappedForDropdown: otherMappedForDropdown } = useGetEmissionsFromQuestion({
    question: { apiKey: 'calculation-building-emissions-other' },
  });
  const { mappedForDropdown: vehiclesMappedForDropdown } = useGetEmissionsFromQuestion({
    question: { apiKey: 'calculation-travel-emissions-company-vehicles-total-fuel-consumption' },
  });
  const allRefrigerants = useSelector(getRefrigerantsSelector);
  const deicingChemicalTypes = useSelector(getDeicingChemicalTypesSelector);

  if (
    questionFormatName === 'other' ||
    questionFormatName === 'sold-products-fuels' ||
    questionFormatName === 'on-site-power-generation' ||
    questionFormatName === 'boilers-and-furnaces' ||
    questionFormatName === 'on-site-water-treatment'
  ) {
    return otherMappedForDropdown || [];
  }
  if (questionFormatName === 'refrigerants') {
    return allRefrigerants;
  }
  if (questionFormatName === 'vehicles') {
    return vehiclesMappedForDropdown;
  }
  if (questionFormatName === 'deicing') {
    return deicingChemicalTypes;
  }
  if (questionFormatName === 'purchased-goods-and-services') {
    return purchasedGoodsAndServicesSpendCategories;
  }
  if (questionFormatName === 'capital-goods') {
    return capitalGoodsSpendCategories;
  }
  if (questionFormatName === 'waste-generated') {
    return wasteTypes || [];
  }
  return [];
};
